import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { MembersApi } from "@/api/perkpoint/members";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programId: string;
  memberId: string;
  memberName: string;
  available: number;
  onAdjusted: () => void;
};

export function AdjustMemberPointsDialog({
  open,
  onOpenChange,
  programId,
  memberId,
  memberName,
  available,
  onAdjusted,
}: Props) {
  const [direction, setDirection] = useState<"add" | "remove">("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const mutation = useMutation({
    mutationFn: (input: { points: number; reason: string }) =>
      MembersApi.adjustPoints(programId, memberId, input),
    onSuccess: (result) => {
      const verb = result.points > 0 ? "added" : "removed";
      toast.success(`${Math.abs(result.points).toLocaleString("en-IN")} points ${verb}`);
      setAmount("");
      setReason("");
      setDirection("add");
      onAdjusted();
      onOpenChange(false);
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not adjust points")),
  });

  const submit = () => {
    const points = Number(amount);
    if (!Number.isSafeInteger(points) || points <= 0) {
      toast.error("Enter a whole number of points greater than zero");
      return;
    }
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error("A reason is required");
      return;
    }
    if (direction === "remove" && points > available) {
      toast.error(`Only ${available.toLocaleString("en-IN")} points are available`);
      return;
    }
    mutation.mutate({
      points: direction === "remove" ? -points : points,
      reason: trimmed,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust points</DialogTitle>
          <DialogDescription>
            Add or remove points for {memberName}. Available now: {available.toLocaleString("en-IN")}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {(["add", "remove"] as const).map((item) => (
              <button
                key={item}
                type="button"
                className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                  direction === item
                    ? "border-[#1B7A45] bg-[#eef8f2] text-[#145C3C]"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
                onClick={() => setDirection(item)}
              >
                {item === "add" ? "Add points" : "Remove points"}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adjust-points-amount">Points</Label>
            <Input
              id="adjust-points-amount"
              inputMode="numeric"
              value={amount}
              onChange={(event) => setAmount(event.target.value.replace(/\D/g, ""))}
              placeholder="100"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adjust-points-reason">Reason</Label>
            <Textarea
              id="adjust-points-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Goodwill credit, correction, or similar"
              className="min-h-[88px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-[#1B7A45] text-white hover:bg-[#16653A]"
            disabled={mutation.isPending}
            onClick={submit}
          >
            {mutation.isPending ? "Saving…" : direction === "add" ? "Add points" : "Remove points"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
